import React, { useState, useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import useEmpresa from "../../../hooks/useEmpresa";
import IngresoActions from "../../../stores/ingreso/Actions";
import INITIAL_STATE from "../../../stores/ingreso/InitialState";
import CustomLayout from "../../../components/CustomLayout/CustomLayout";
import CustomButton from "../../../components/CustomButton/CustomButton";
import CustomSeparator from "../../../components/Common/CustomSeparator";
import { Section } from "../../../components/CustomLayout/CustomLayoutStyles";
import TittleLine from "../../../components/Common/TittleLine";
import CustomTable from "../../../components/CustomTable/CustomTable";
import CustomInput from "../../../components/CustomForm/CustomInput";
import CustomSelect from "../../../components/CustomForm/CustomSelect";
import {
  FormWrapper,
  Form,
  FormGroup,
} from "../../../components/CustomForm/FormStyles";
import ModalLoader from "../../../components/Common/ModalLoader";
import CustomAlert from "../../../components/Common/CustomAlert";
import CheckRender from "../../../components/Common/CheckRender";
import Pagination from "../../../components/CustomTable/Pagination";
import Colors from "../../../themes/Colors";
import moment from "moment";
import { DATE_FORMATS, EMPRESA } from "../../../utils/Constant";
import { cleanNumber } from "../../../utils/NumberUtil";
import {
  TIPO_PSS_NO_MEDICO_JIRA,
  PLAN_PDSS_JIRA,
  HOMOLOGACION_ESTATUS_INGRESO,
} from "../utils/IngresoConstant";

const getColumns = (empresa, goToHojaQuirurgica) => [
  {
    title: "ID",
    dataIndex: "id",
    key: "id",
  },
  {
    title: "Número de ingreso",
    dataIndex: "numeroIngreso",
    key: "numeroIngreso",
    render: (text, record) => (
      <button
        type="button"
        onClick={() => goToHojaQuirurgica(record)}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          color: empresa?.COLOR_SECUNDARIO,
          textDecoration: "underline",
          cursor: "pointer",
          fontFamily: "inherit",
          fontSize: "inherit",
        }}
      >
        {text}
      </button>
    ),
  },
  { title: "Cédula o Pasaporte", dataIndex: "documento", key: "documento" },
  {
    title: "Número de afiliado",
    dataIndex: "codigoPersonaHospitalizada",
    key: "codigoPersonaHospitalizada",
  },
  {
    title: "Nombre del paciente",
    dataIndex: "nombreDelPaciente",
    key: "nombreDelPaciente",
  },
  {
    title: "Fecha de Ingreso",
    dataIndex: "fechaIngreso",
    key: "fechaIngreso",
    render: (text) =>
      text ? moment(text).format(DATE_FORMATS.middledash_ymd) : "N/A",
  },
  {
    title: "Fecha de egreso",
    dataIndex: "fechaSalida",
    key: "fechaSalida",
    render: (text) =>
      text ? moment(text).format(DATE_FORMATS.middledash_ymd) : "N/A",
  },
  {
    title: "Estado",
    dataIndex: "estatus",
    key: "estatus",
    render: (text) => HOMOLOGACION_ESTATUS_INGRESO[text] || "N/A",
  },
  {
    title: "Acción",
    key: "accion",
    render: (_, record) => (
      <CustomButton
        label="Agregar"
        onClick={() => goToHojaQuirurgica(record)}
        height="30px"
        fontSize="12px"
        bgColor={empresa?.COLOR_SECUNDARIO}
        hoverColor={empresa?.COLOR_PRINCIPAL}
      />
    ),
  },
];

const PacientesIngresadosScreen = (props) => {
  const {
    formPacientesIngresadosData,
    consultarPacientesIngresadosLoading,
    authUserData,
    formPacientesIngresados,
    consultarPacientesIngresados,
    formHojaQuirurgicaData,
    formHojaQuirurgica,
  } = props;

  const history = useHistory();
  const empresa = useEmpresa();
  const prestador = authUserData?.prestador;
  const isPrimera = empresa?.CODIGO === EMPRESA.PRIMERA.CODIGO;

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!authUserData || Object.keys(authUserData).length === 0) {
      return;
    }

    if (!isPrimera || !authUserData?.prestador?.permiteHojaQuirurgica) {
      history.push("/dashboard");
      return;
    }

    if (!hasInitialized.current && !consultarPacientesIngresadosLoading) {
      hasInitialized.current = true;
      const initialData = INITIAL_STATE.formPacientesIngresadosData;
      formPacientesIngresados(initialData);

      // Initial load
      consultarPacientesIngresados({
        formPacientesIngresadosData: initialData,
        numeroMedico: prestador?.codigo,
        params: {},
        formHandler: formPacientesIngresados,
      });
    }
  }, [
    authUserData,
    isPrimera,
    prestador,
    history,
    formPacientesIngresados,
    consultarPacientesIngresados,
    consultarPacientesIngresadosLoading,
  ]);

  const onStateChange =
    (key, format = null) =>
    (value) => {
      formPacientesIngresados({
        ...formPacientesIngresadosData,
        [key]: format ? format(value) : value,
      });
    };

  const buildParams = (formData = formPacientesIngresadosData) => {
    return {
      CodigoAsegurado: formData.codigoAsegurado,
      NumeroIngreso: formData.numeroIngreso,
      Documento: formData.documento,
      FechaIngreso: formData.fechaIngreso,
      FechaSalida: formData.fechaSalida,
      NombrePaciente: formData.nombrePaciente,
    };
  };

  const handleSearch = () => {
    const params = buildParams();

    consultarPacientesIngresados({
      formPacientesIngresadosData,
      numeroMedico: prestador?.codigo,
      params,
      formHandler: formPacientesIngresados,
    });
  };

  const handleClear = () => {
    const clearedData = {
      ...formPacientesIngresadosData,
      tipoDocumento: "",
      documento: "",
      numeroIngreso: "",
      codigoAsegurado: "",
      nombrePaciente: "",
      fechaIngreso: null,
      fechaSalida: null,
      paginaActual: 1,
    };
    formPacientesIngresados(clearedData);

    consultarPacientesIngresados({
      formPacientesIngresadosData: clearedData,
      numeroMedico: prestador?.codigo,
      params: {},
      formHandler: formPacientesIngresados,
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const goToHojaQuirurgica = (paciente) => {
    const isDifferentIngreso =
      formHojaQuirurgicaData?.numeroIngreso !== paciente.numeroIngreso;

    const baseData = isDifferentIngreso
      ? {
          ...INITIAL_STATE.formHojaQuirurgicaData,
          medicosAdicionales: [],
          documentos: {
            hojaQuirurgica: null,
            hojaAnestesia: null,
            estudiosRealizados: null,
            historiaClinica: null,
            otros: null,
          },
          alertConfig: {},
        }
      : formHojaQuirurgicaData;

    formHojaQuirurgica({
      ...baseData,
      nombreAfiliado: paciente.nombreDelPaciente,
      fechaIngreso: paciente.fechaIngreso
        ? moment(paciente.fechaIngreso).format(DATE_FORMATS.middledash_ymd)
        : "",
      numeroCarnet: paciente.numeroPlastico,
      tipoServicio: paciente.descripcionServicio,
      nombrePss: prestador?.nombre,
      plan: PLAN_PDSS_JIRA,
      tipoPss: TIPO_PSS_NO_MEDICO_JIRA,
      codigoPss: prestador?.codigo,
      medicoTratantePrincipal: paciente.medicosTratantes,
      numeroIngreso: paciente.numeroIngreso,
      diagnostico: paciente.descripcionDiagnostico || "",
    });
    history.push("/hoja-quirurgica");
  };

  const columns = useMemo(() => getColumns(empresa, goToHojaQuirurgica), [empresa, goToHojaQuirurgica]);

  return (
    <CustomLayout
      showMenuNav={true}
      footerPosition={
        formPacientesIngresadosData?.pacientes?.length > 0
          ? "relative"
          : "fixed"
      }
    >
      <Section>
        <TittleLine title={"Pacientes ingresados"} weight={"bold"} />
      </Section>

      <Section padding={"5px"}>
        <FormWrapper borderColor={Colors.transparent}>
          <Form justifyContent={"space-between"} flexDirection="column">
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                columnGap: "30px",
                rowGap: "20px",
                marginBottom: "20px",
                width: "100%",
              }}
            >
              <FormGroup width="calc(33.33% - 20px)">
                <CustomSelect
                  label={"Tipo de documento"}
                  placeholder={"Seleccionar tipo"}
                  options={[
                    { label: "Cédula", value: "CEDULA" },
                    { label: "Pasaporte", value: "PASAPORTE" },
                  ]}
                  value={formPacientesIngresadosData.tipoDocumento}
                  onChange={onStateChange("tipoDocumento")}
                  width="100%"
                />
              </FormGroup>
              <FormGroup width="calc(33.33% - 20px)">
                <CustomInput
                  label={"Número de Documento"}
                  placeholder={"Número de Documento"}
                  value={formPacientesIngresadosData.documento}
                  onChange={onStateChange("documento")}
                  width="100%"
                />
              </FormGroup>
              <FormGroup width="calc(33.33% - 20px)">
                <CustomInput
                  label={"Número de ingreso"}
                  placeholder={"Número de ingreso"}
                  value={formPacientesIngresadosData.numeroIngreso}
                  onChange={onStateChange("numeroIngreso", cleanNumber)}
                  width="100%"
                />
              </FormGroup>
              <FormGroup width="calc(33.33% - 20px)">
                <CustomInput
                  label={"Número de Afiliado"}
                  placeholder={"Número de Afiliado"}
                  value={formPacientesIngresadosData.codigoAsegurado}
                  onChange={onStateChange("codigoAsegurado", cleanNumber)}
                  width="100%"
                />
              </FormGroup>
              <FormGroup width="calc(33.33% - 20px)">
                <CustomInput
                  label={"Nombre del paciente / Afiliado"}
                  placeholder={"Nombre del paciente / Afiliado"}
                  value={formPacientesIngresadosData.nombrePaciente}
                  onChange={onStateChange("nombrePaciente")}
                  width="100%"
                />
              </FormGroup>
              <FormGroup width="calc(33.33% - 20px)">
                <CustomInput
                  type="date"
                  label={"Fecha de ingreso"}
                  value={formPacientesIngresadosData.fechaIngreso}
                  onChange={onStateChange("fechaIngreso")}
                  width="100%"
                />
              </FormGroup>
              <FormGroup width="calc(33.33% - 20px)">
                <CustomInput
                  type="date"
                  label={"Fecha de egreso"}
                  value={formPacientesIngresadosData.fechaSalida}
                  onChange={onStateChange("fechaSalida")}
                  width="100%"
                />
              </FormGroup>
            </div>
            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "flex-start",
              }}
            >
              <CustomButton
                label={"Buscar"}
                onClick={handleSearch}
                width={"150px"}
                bgColor={empresa?.COLOR_SECUNDARIO}
              />
              <CustomButton
                label={"Limpiar"}
                onClick={handleClear}
                width={"150px"}
                bgColor={Colors.huMyHealth}
              />
            </div>
          </Form>
        </FormWrapper>
      </Section>

      <CustomSeparator height="30px" />

      <Section>
        <CustomTable
          columns={columns}
          dataSource={formPacientesIngresadosData.pacientes}
          showPagination={false}
          pageSize={formPacientesIngresadosData.tamanoPagina}
          headerColor={empresa?.COLOR_PRINCIPAL}
        />
      </Section>
      <CustomSeparator height={"20px"} />
      <Section>
        <Pagination
          currentPage={formPacientesIngresadosData.paginaActual}
          itemsPerPage={formPacientesIngresadosData.tamanoPagina}
          totalItems={formPacientesIngresadosData.totalRegistros}
          onPageChange={(page) => {
            const updatedForm = {
              ...formPacientesIngresadosData,
              paginaActual: page,
            };
            formPacientesIngresados(updatedForm);

            const params = buildParams(updatedForm);
            consultarPacientesIngresados({
              formPacientesIngresadosData: updatedForm,
              numeroMedico: prestador?.codigo,
              params,
              formHandler: formPacientesIngresados,
            });
          }}
        />
      </Section>

      <CheckRender allowed={consultarPacientesIngresadosLoading}>
        <ModalLoader />
      </CheckRender>

      <CheckRender allowed={formPacientesIngresadosData.showAlert}>
        <CustomAlert {...formPacientesIngresadosData.alertConfig} />
      </CheckRender>
    </CustomLayout>
  );
};

const mapStateToProps = (state) => ({
  authUserData: state.auth.authUserData,
  formPacientesIngresadosData: state.ingreso.formPacientesIngresadosData,
  consultarPacientesIngresadosLoading:
    state.ingreso.consultarPacientesIngresadosLoading,
  formHojaQuirurgicaData: state.ingreso.formHojaQuirurgicaData,
});

const mapDispatchToProps = {
  formPacientesIngresados: IngresoActions.formPacientesIngresados,
  consultarPacientesIngresados: IngresoActions.consultarPacientesIngresados,
  formHojaQuirurgica: IngresoActions.formHojaQuirurgica,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(PacientesIngresadosScreen);
